import React from 'react';
import { kiPrefilledPaths } from './calc.js';
import { I18N_LOCALES, currentLocale, loadLocale, t } from './i18n.js';
/* ============================================================================
   cloudtec Plattform-Konfigurator — Atom components
   ============================================================================ */

const { useState, useRef, useEffect, useMemo, useCallback, useContext, createContext } = React;

// ============================================================================
// CalcOverlay — Dev-Mode-Context: zeigt marginalen PT-Beitrag pro Element
// ============================================================================
const CalcOverlayContext = createContext({ enabled: false, baseState: null });
const useCalcOverlay = () => useContext(CalcOverlayContext);

/** Kleines Badge, das einen PT-Wert anzeigt. Rendert nur wenn Overlay aktiv. */
const PtBadge = ({ value, dim, hideZero = true, prefix }) => {
  const { enabled } = useCalcOverlay();
  if (!enabled || value == null || !Number.isFinite(value)) return null;
  const v = Math.round(value);
  if (v === 0 && hideZero) return null;
  const sign = v > 0 ? '+' : '';
  return (
    <span className={`pt-badge${dim ? ' is-dim' : ''}${v < 0 ? ' is-neg' : ''}`}>
      {prefix}{sign}{v} PT
    </span>
  );
};

/** Wrapper für Slider-Meta (total + pro Einheit). */
const PtSliderMeta = ({ total, perUnit }) => {
  const { enabled } = useCalcOverlay();
  if (!enabled) return null;
  const t = Math.round(total || 0);
  const p = Math.round(perUnit || 0);
  return (
    <div className="pt-slider-meta">
      <span className="pt-badge">{t} PT total</span>
      <span className="pt-badge is-dim">+{p} PT pro Einheit</span>
    </div>
  );
};

/** CALC-Toggle neben dem LanguageSwitcher. Nur sichtbar im Dev-Mode. */
const CalcSwitch = ({ enabled, onChange }) => (
  <div className="lang-switch" role="group" aria-label="Calculation overlay">
    <button
      type="button"
      className={`lang-switch__btn${enabled ? ' is-active' : ''}`}
      onClick={() => onChange(!enabled)}
      aria-pressed={enabled}
      title="Berechnungs-Overlay (Dev-Mode)"
    >
      CALC
    </button>
  </div>
);


// --- Tooltip / Help Hint -----------------------------------------------------
const HelpHint = ({ text, size = 14 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  if (!text) return null;
  return (
    <span ref={ref} className="helphint" data-open={open ? 'true' : 'false'}>
      <span
        role="button"
        tabIndex={0}
        className="helphint__btn"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(!open); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpen(!open); } }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-label="Mehr Informationen"
      >
        <i className="fa-regular fa-circle-question" style={{ fontSize: size - 2 }}></i>
      </span>
      {open && <span className="helphint__pop" role="tooltip">{text}</span>}
    </span>
  );
};

// --- Logos --------------------------------------------------------------------
const Logo = ({ height = 22 }) => (
  <svg viewBox="0 0 401.23 62.36" style={{ height, width: 'auto', display: 'block' }} fill="currentColor">
    <g>
      <path d="M12.79,58.86a26.43,26.43,0,0,1-9.3-9.43A25,25,0,0,1,3.49,24a26.46,26.46,0,0,1,9.3-9.44A23.87,23.87,0,0,1,25.29,11a22.42,22.42,0,0,1,11.15,2.82,25.33,25.33,0,0,1,12.4,16.81H35.66A10.6,10.6,0,0,0,34,27.54a11.44,11.44,0,0,0-3.49-3.11,9.92,9.92,0,0,0-5.24-1.34,11.14,11.14,0,0,0-8.67,3.83,14.08,14.08,0,0,0-3.44,9.77,14.08,14.08,0,0,0,3.44,9.77,11.17,11.17,0,0,0,8.67,3.83A10,10,0,0,0,30.53,49,11.44,11.44,0,0,0,34,45.84a10.51,10.51,0,0,0,1.64-3.12H48.84a25.27,25.27,0,0,1-12.4,16.81,22.33,22.33,0,0,1-11.15,2.83A23.79,23.79,0,0,1,12.79,58.86Z"/>
      <path d="M66.77,61.12H53.59V0H66.77Z"/>
      <path d="M84.28,58.86A26.43,26.43,0,0,1,75,49.43,25,25,0,0,1,75,24a26.46,26.46,0,0,1,9.3-9.44,24.15,24.15,0,0,1,25,0,26.54,26.54,0,0,1,9.3,9.44,25,25,0,0,1,0,25.48,26.51,26.51,0,0,1-9.3,9.43,24.09,24.09,0,0,1-25,0Zm21.18-12.4a14.08,14.08,0,0,0,3.44-9.77,14.08,14.08,0,0,0-3.44-9.77,11.74,11.74,0,0,0-17.35,0,14.08,14.08,0,0,0-3.44,9.77,14.08,14.08,0,0,0,3.44,9.77,11.74,11.74,0,0,0,17.35,0Z"/>
      <path d="M136.14,59.73a18.14,18.14,0,0,1-7-7.48,23.77,23.77,0,0,1-2.52-11.15V12.26h13.18V40a10.36,10.36,0,0,0,2.81,7.32,9.68,9.68,0,0,0,14,0A10.38,10.38,0,0,0,159.35,40V12.26h13.18V61.12h-9.89l-2.13-4.79H160a18.44,18.44,0,0,1-5.13,4,16.48,16.48,0,0,1-8.34,2.06A20.28,20.28,0,0,1,136.14,59.73Z"/>
      <path d="M189,59a24.11,24.11,0,0,1-8.43-9.25,27.24,27.24,0,0,1-3.15-13.07,27.31,27.31,0,0,1,3.15-13.08A24.08,24.08,0,0,1,189,14.37,20.64,20.64,0,0,1,200.31,11a17.3,17.3,0,0,1,6,1,19.87,19.87,0,0,1,4.55,2.25,8.38,8.38,0,0,1,2.38,2.21h.48V0h13.18V61.12H217l-2.13-4.79h-.49a18.76,18.76,0,0,1-14.05,6A20.73,20.73,0,0,1,189,59Zm18.85-10.44a12.41,12.41,0,0,0,4.21-4.84,15.11,15.11,0,0,0,1.6-7,15.11,15.11,0,0,0-1.6-7,12.41,12.41,0,0,0-4.21-4.84,10.37,10.37,0,0,0-11.44,0,12.41,12.41,0,0,0-4.21,4.84,15.11,15.11,0,0,0-1.6,7,15.11,15.11,0,0,0,1.6,7,12.41,12.41,0,0,0,4.21,4.84,10.37,10.37,0,0,0,11.44,0Z"/>
      <path d="M232.9,0h13.18V12.26h10.17V23.85H246.08V43.2a6.86,6.86,0,0,0,1.79,5.18,6.89,6.89,0,0,0,5,1.72,7.55,7.55,0,0,0,3.39-.67V61.31a23.82,23.82,0,0,1-6.68,1,16,16,0,0,1-12-4.79q-4.65-4.78-4.65-13V0Z"/>
      <path d="M308.6,41.38H273a12.09,12.09,0,0,0,4.21,6.9,11.9,11.9,0,0,0,7.61,2.49,11.49,11.49,0,0,0,8.82-3.64h13.86a25.91,25.91,0,0,1-12.41,12.93A22.33,22.33,0,0,1,285,62.36a24.44,24.44,0,0,1-12.69-3.45,25.66,25.66,0,0,1-12.6-22.22A25,25,0,0,1,263.19,24a26,26,0,0,1,9.26-9.44,24.25,24.25,0,0,1,24.66-.24,23.93,23.93,0,0,1,8.82,9,25.23,25.23,0,0,1,3.25,12.69A20,20,0,0,1,308.6,41.38ZM277.39,25.15A12.66,12.66,0,0,0,273,32.09h23.45a13.06,13.06,0,0,0-4.31-6.9,11.21,11.21,0,0,0-7.32-2.58A11.61,11.61,0,0,0,277.39,25.15Z"/>
      <path d="M348.19,43.64a13.64,13.64,0,0,1-1.29,2.2A11.56,11.56,0,0,1,343.41,49a9.94,9.94,0,0,1-5.24,1.34,11.16,11.16,0,0,1-8.67-3.83,14,14,0,0,1-3.44-9.77,14,14,0,0,1,3.44-9.77,11.12,11.12,0,0,1,8.67-3.83,9.94,9.94,0,0,1,5.24,1.34,11.56,11.56,0,0,1,3.49,3.11,14,14,0,0,1,1.29,2.19l9.05-8.94a24.85,24.85,0,0,0-7.92-6.95A22.46,22.46,0,0,0,338.17,11a23.85,23.85,0,0,0-12.5,3.5,26.38,26.38,0,0,0-9.3,9.44,25,25,0,0,0,0,25.48,26.38,26.38,0,0,0,9.3,9.44,23.84,23.84,0,0,0,12.5,3.49,22.45,22.45,0,0,0,11.15-2.82,24.85,24.85,0,0,0,7.92-6.95Z"/>
    </g>
    <rect x="357.4" y="18.53" width="36.31" height="36.31" transform="translate(84.06 276.31) rotate(-45)" fill="#02ffa1"/>
  </svg>
);

const Diamond = ({ size = 56 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <rect x="20" y="20" width="60" height="60" transform="rotate(45 50 50)" fill="#02ffa1"/>
  </svg>
);

// --- Button ------------------------------------------------------------------
const Btn = ({ children, icon, iconRight, kind, size, variant, onClick, disabled, type = 'button', title, 'aria-label': ariaLabel, className }) => (
  <button
    type={type}
    className={`btn${className ? ` ${className}` : ''}`}
    data-kind={kind}
    data-size={size}
    data-variant={variant}
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={ariaLabel}
  >
    {icon && <i className={`fa-solid fa-${icon}`}></i>}
    {children && <span>{children}</span>}
    {iconRight && <i className={`fa-solid fa-${iconRight}`}></i>}
  </button>
);

// --- Field wrapper ------------------------------------------------------------
const Field = ({ label, hint, error, children, required, help }) => (
  <div className="field">
    {label && (
      <label className="field__label">
        {label}{required && <span style={{ color: 'var(--danger-60)', marginLeft: 4 }}>*</span>}{help && <HelpHint text={help}/>}
      </label>
    )}
    {children}
    {hint && !error && <div className="field__hint">{hint}</div>}
    {error && <div className="field__error"><i className="fa-solid fa-circle-exclamation"></i>{error}</div>}
  </div>
);

// --- Text inputs --------------------------------------------------------------
const TextInput = ({ value, onChange, invalid, ...rest }) => (
  <input
    type="text"
    className={`input${invalid ? ' is-invalid' : ''}`}
    value={value || ''}
    onChange={(e) => onChange?.(e.target.value)}
    {...rest}
  />
);

const TextArea = ({ value, onChange, invalid, max, showCount, autoGrow, maxHeight, style, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!autoGrow || !ref.current) return;
    const el = ref.current;
    el.style.height = 'auto';
    const cap = typeof maxHeight === 'number' ? maxHeight : Infinity;
    const next = Math.min(el.scrollHeight, cap);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > cap ? 'auto' : 'hidden';
  }, [value, autoGrow, maxHeight]);
  return (
    <div>
      <textarea
        ref={ref}
        className={`textarea${invalid ? ' is-invalid' : ''}`}
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        maxLength={max}
        style={style}
        {...rest}
      ></textarea>
      {showCount && (
        <div className="field__counter">{(value || '').length}{max ? ` / ${max}` : ''}</div>
      )}
    </div>
  );
};

// --- Card grid (multi/single select) -----------------------------------------
const OptCard = ({ icon, title, sub, selected, disabled, ai, radio, onClick, badge, help }) => (
  <button
    type="button"
    className={`optcard${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}
    data-radio={radio ? 'true' : 'false'}
    onClick={disabled ? undefined : onClick}
    aria-pressed={selected}
    disabled={disabled}
  >
    <span className="optcard__check">
      {selected && (radio
        ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor', display: 'block' }}></span>
        : <i className="fa-solid fa-check"></i>)}
    </span>
    {ai && <span className="optcard__ai" title="Vorschlag der KI — bitte prüfen."></span>}
    <span className="optcard__row">
      {icon && <span className="optcard__icon"><i className={`fa-solid fa-${icon}`}></i></span>}
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span className="optcard__title">{title}{help && <HelpHint text={help}/>}</span>
        {sub && <span className="optcard__sub">{sub}</span>}
      </span>
    </span>
    {badge}
  </button>
);

// --- Segmented control --------------------------------------------------------
// ptValues: optional Map<optionId, number> — wenn gesetzt, wird pro Segment ein PT-Badge gerendert.
const Segmented = ({ options, value, onChange, size, stack, ptValues }) => (
  <div className="segmented" data-size={size} data-stack={stack ? 'true' : undefined}>
    {options.map((opt) => (
      <button
        type="button"
        key={opt.id}
        className={`segmented__seg${value === opt.id ? ' is-active' : ''}`}
        onClick={() => onChange?.(opt.id)}
      >
        <span>{opt.label}</span>
        {opt.sub && <span className="seg-sub">{opt.sub}</span>}
        {ptValues && <PtBadge value={ptValues[opt.id]} dim={value !== opt.id} hideZero={false}/>}
      </button>
    ))}
  </div>
);

// --- Radio list ---------------------------------------------------------------
const RadioList = ({ options, value, onChange, ptValues }) => (
  <div className="radiolist">
    {options.map((opt) => (
      <button
        type="button"
        key={opt.id}
        className={`radiolist__opt${value === opt.id ? ' is-selected' : ''}`}
        onClick={() => onChange?.(opt.id)}
        aria-pressed={value === opt.id}
      >
        <span className="radiolist__dot"></span>
        <span className="radiolist__body">
          <span className="radiolist__title">{opt.title}</span>
          {opt.sub && <span className="radiolist__sub">{opt.sub}</span>}
        </span>
        {ptValues && <PtBadge value={ptValues[opt.id]} dim={value !== opt.id} hideZero={false}/>}
      </button>
    ))}
  </div>
);

// --- Chip group ---------------------------------------------------------------
// ptValues: optional Map<optionId, number> — pro Chip ein PT-Badge.
const ChipGroup = ({ options, value = [], onChange, single = false, allowUnknown, ptValues }) => {
  const isSelected = (id) => single ? value === id : (value || []).includes(id);
  const toggle = (id) => {
    if (single) { onChange?.(value === id ? null : id); return; }
    if (isSelected(id)) onChange?.(value.filter(v => v !== id));
    else onChange?.([...(value || []), id]);
  };
  return (
    <div className="chipgroup">
      {options.map(opt => (
        <button
          type="button"
          key={opt.id}
          className={`chip${isSelected(opt.id) ? ' is-selected' : ''}`}
          onClick={() => toggle(opt.id)}
          aria-pressed={isSelected(opt.id)}
          data-tone={opt.tone}
        >
          {opt.icon && <i className={`fa-solid fa-${opt.icon}`}></i>}
          {opt.label}
          {opt.help && <HelpHint text={opt.help} size={12}/>}
          {ptValues && <PtBadge value={ptValues[opt.id]} dim={!isSelected(opt.id)}/>}
        </button>
      ))}
    </div>
  );
};

// --- Slider -------------------------------------------------------------------
const Slider = ({ value, onChange, min = 0, max = 100, step = 1, disabled, format, recommended, recommendedLabel, hint }) => {
  const v = value == null ? min : value;
  const pct = ((v - min) / (max - min)) * 100;
  const recPct = recommended != null ? ((recommended - min) / (max - min)) * 100 : null;
  return (
    <div className="slider-row">
      <div className="slider-meta">
        <span className="slider-value">{format ? format(v) : v.toLocaleString('de-CH')}</span>
        {recommended != null && (
          <span className="slider-recommend">
            <i className="fa-solid fa-circle-dot" style={{ fontSize: 10, color: 'var(--text-color-muted)' }}></i>
            <span>Empfohlen: <strong>{format ? format(recommended) : recommended.toLocaleString('de-CH')}</strong></span>
          </span>
        )}
      </div>
      <input
        type="range"
        className="slider"
        min={min} max={max} step={step}
        value={v}
        disabled={disabled}
        onChange={(e) => onChange?.(Number(e.target.value))}
        style={{ '--slider-pct': pct + '%' }}
      />
      {recPct != null && (
        <div className="slider-marker">
          <span className="mark" style={{ left: recPct + '%' }} data-label={recommendedLabel || ''}></span>
        </div>
      )}
      <div className="slider-meta" style={{ marginTop: recPct != null ? 22 : 0 }}>
        <span className="muted">{format ? format(min) : min}</span>
        <span className="muted">{format ? format(max) : max}</span>
      </div>
      {hint && <div className="field__hint" style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  );
};

// --- Collapsible block --------------------------------------------------------
const Collapsible = ({ num, title, hint, defaultOpen = false, children, summary, headAction, consultActive }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="collapsible" data-open={open ? 'true' : 'false'} data-consult={consultActive ? 'true' : 'false'}>
      <div className="collapsible__head-wrap">
        <button type="button" className="collapsible__head" onClick={() => setOpen(!open)} aria-expanded={open}>
          {num != null && <span className="collapsible__num">{num}</span>}
          <i className="fa-solid fa-chevron-right collapsible__chev"></i>
          <span>{title}</span>
          {(hint || summary) && <span className="collapsible__hint">{summary || hint}</span>}
        </button>
        {headAction && <div className="collapsible__action">{headAction}</div>}
      </div>
      <div className="collapsible__body">
        {consultActive && (
          <div className="consult-banner">
            <i className="fa-solid fa-comments"></i>
            <span><strong>{t('p2.consult_active')}.</strong> {t('p2.consult_banner_body')}</span>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

// --- "Weiss nicht / Beratung" section toggle ---------------------------------
const ConsultToggle = ({ active, onClick }) => (
  <button type="button" className={`consult-btn${active ? ' is-active' : ''}`} onClick={onClick}>
    <i className={`fa-${active ? 'solid' : 'regular'} fa-circle-question`}></i>
    <span>{active ? t('p2.consult_active') : t('p2.consult_inactive')}</span>
  </button>
);

// --- Banner -------------------------------------------------------------------
const Banner = ({ tone = 'info', title, children, icon, onDismiss }) => {
  const cls = tone === 'warning' ? 'banner is-warning'
            : tone === 'success' ? 'banner is-success'
            : tone === 'next-steps' ? 'banner is-next-steps'
            : 'banner';
  const ic = icon || (tone === 'warning' ? 'triangle-exclamation' : tone === 'success' ? 'circle-check' : tone === 'next-steps' ? 'wand-magic-sparkles' : 'circle-info');
  return (
    <div className={cls}>
      <i className={`fa-solid fa-${ic}`}></i>
      <div style={{ flex: 1 }}>
        {title && <div className="banner__title">{title}</div>}
        {children && <div className="banner__body">{children}</div>}
      </div>
      {onDismiss && (
        <button type="button" className="banner__close" onClick={onDismiss} aria-label={t('common.close')}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      )}
    </div>
  );
};

// --- "Weiss nicht" link helper -----------------------------------------------
const UnknownLink = ({ active, onClick, label = "Weiss ich nicht" }) => (
  <button
    type="button"
    className="helper-link"
    onClick={onClick}
    style={{ color: active ? 'var(--warning-70)' : undefined }}
  >
    <i className={`fa-${active ? 'solid' : 'regular'} fa-circle-question`}></i>
    {active ? `„${label}" ausgewählt — entfernen` : label}
  </button>
);

// --- Pill --------------------------------------------------------------------
const Pill = ({ tone, children, icon, hideMobile }) => (
  <span className={`pill${hideMobile ? ' is-hide-mobile' : ''}`} data-tone={tone}>
    {!icon && tone && <span className="dot"></span>}
    {icon && <i className={`fa-solid fa-${icon}`}></i>}
    {children}
  </span>
);

// --- Language Switcher --------------------------------------------------------
const LanguageSwitcher = () => {
  const [loc, setLoc] = useState(currentLocale());
  useEffect(() => {
    const h = (e) => setLoc(e.detail.locale);
    document.addEventListener('locale-changed', h);
    return () => document.removeEventListener('locale-changed', h);
  }, []);
  const switchTo = (l) => { if (l !== loc) loadLocale(l, { persist: true }); };
  return (
    <div className="lang-switch" role="group" aria-label="Language">
      {(I18N_LOCALES || ['de','en']).map(l => (
        <button
          key={l}
          type="button"
          className={`lang-switch__btn${l === loc ? ' is-active' : ''}`}
          onClick={() => switchTo(l)}
          aria-pressed={l === loc}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

// --- KI-Banner (shown on p1/p2/p3 after AI prefill, until dismissed) ----------
const KiBanner = ({ state, onDismiss }) => {
  const paths = kiPrefilledPaths ? kiPrefilledPaths(state) : [];
  const meta = state?.ki_metadata;
  const dismissed = state?.ki_metadata?.bannerDismissed;
  if (!meta || !meta.feldConfidence || paths.length === 0 || dismissed) return null;
  const textKey = paths.length === 1 ? 'ki_banner.text_count_one' : 'ki_banner.text_count_other';
  return (
    <div className="kibanner" role="status">
      <i className="fa-solid fa-wand-magic-sparkles"></i>
      <div className="kibanner__body">
        <div className="kibanner__title">{t('ki_banner.title')}</div>
        <div className="kibanner__text" dangerouslySetInnerHTML={{
          __html: t(textKey, { count: `<strong>${paths.length}</strong>` }),
        }}/>
        {meta.begruendung ? (
          <details className="kibanner__details">
            <summary>{t('ki_banner.show_reasoning')}</summary>
            <p>{meta.begruendung}</p>
          </details>
        ) : null}
      </div>
      <button type="button" className="kibanner__dismiss" onClick={onDismiss} aria-label={t('ki_banner.dismiss')}>
        <i className="fa-solid fa-xmark"></i>
      </button>
    </div>
  );
};

// --- Privacy Modal (shown before KI-Call) ------------------------------------
const PrivacyModal = ({ open, onAccept, onSkip, onClose }) => {
  const [remember, setRemember] = useState(false);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className="modal modal--violet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="privacy-title">
        <div className="modal__head">
          <h3 id="privacy-title" className="modal__title">
            <i className="fa-solid fa-shield-halved" style={{ marginRight: 10, color: 'var(--tertiary-50)' }}></i>
            {t('privacy_modal.title')}
          </h3>
        </div>
        <div className="modal__body">
          <p>{t('privacy_modal.intro')}</p>
          <ul style={{ margin: '12px 0', paddingLeft: 20, lineHeight: 1.6 }}>
            <li>{t('privacy_modal.bullet_no_personal')}</li>
            <li>{t('privacy_modal.bullet_skip_possible')}</li>
            <li>{t('privacy_modal.bullet_what_sent')}</li>
          </ul>
          <label className="modal__remember" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, cursor: 'pointer' }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span>{t('privacy_modal.remember')}</span>
          </label>
        </div>
        <div className="modal__foot">
          <Btn kind="quiet" onClick={onSkip}>{t('privacy_modal.decline')}</Btn>
          <Btn kind="primary" icon="check" onClick={() => onAccept({ remember })}>{t('privacy_modal.accept')}</Btn>
        </div>
      </div>
    </div>
  );
};

export {
  Logo, Diamond, HelpHint,
  Btn, Field, TextInput, TextArea,
  OptCard, Segmented, RadioList, ChipGroup, Slider,
  Collapsible, Banner, UnknownLink, ConsultToggle, Pill,
  PrivacyModal, KiBanner, LanguageSwitcher,
  CalcOverlayContext, useCalcOverlay, PtBadge, PtSliderMeta, CalcSwitch,
};
